import { useEffect, useState } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import {
	BsChevronRight,
	BsChevronDown,
	BsChevronUp,
	BsFileRichtext,
	BsArrowRight,
} from 'react-icons/bs';
import Skeleton from 'react-loading-skeleton';

import BayTooltip from '../../../../BayTooltip';

import 'react-loading-skeleton/dist/skeleton.css';
import * as BsIcons from 'react-icons/bs';

import LaunchpadWidgetWrapper from '../../LaunchpadWidgetWrapper';

import '../Widgets.scss';
import { useSelector } from 'react-redux';

interface Link {
	key: string;
	label: string;
	path: string;
	isVisible: boolean;
}

interface OptionGroup {
	title: string;
	designStudio?: string;
	links: Link[];
}

interface Section {
	id: string;
	header: string;
	description: string;
	options: OptionGroup[];
}

interface User {
	platform?: string;
	ActingAsBAY_ACCOUNT_ID?: string | number;
}

interface UserProductAccess {
	client?: boolean;
	social?: boolean;
	socialBayAccessProhibited?: boolean;
	allOfDesignStudioDisabled?: boolean;
	allOfDesignStudioPrintOrderDisabled?: boolean;
}

interface HeaderIcon {
	name: string;
}

interface DefaultWidgetStyles {
	headerIcon?: HeaderIcon;
	headerTitle?: string;
	headerTitleDescription?: string;
	iconColor?: string;
	iconBgColor?: string;
	linkColor?: string;
	sections?: Section[];
}

interface WidgetCustomizationData {
	headerIcon?: HeaderIcon;
	headerTitle?: string;
	headerTitleDescription?: string;
	iconColor?: string;
	iconBgColor?: string;
	linkColor?: string;
	sections?: Section[];
}

interface MarketingWidgetProps {
	widgetDetails?: any[];
	editMode?: boolean;
	widgets?: any[];
	setWidgets?: (widgets: any[]) => void;
	setSnackbar?: (snackbar: any) => void;
	t: (key: string) => string;
	user: User;
	editWidgetHeight?: number | null;
	defaultWidgetStyles?: DefaultWidgetStyles;
	widgetCustomizationData?: WidgetCustomizationData;
	isInPreviewMode?: boolean;
	editModalInputFields?: any;
	setEditModalInputFields?: (fields: any) => void;
	setWidgetCustomizationData?: (data: any) => void;
	setWidgetFinishStatus?: (status: any) => void;
	widgetFinishStatus?: any;
	currentBreakpoint?: string;
	widgetDrawerThemeSettings?: any;
	isLoading?: boolean;
	selectUserProductAccess?: any;
	handleLinkClick?: (e: React.MouseEvent, path: string, useSSO?: boolean) => void;
	selectLaunchpadSettingsData?: any;
	checkAndAdjustLinkPath?: (params: { linkPath: string; activeSection: string }) => { linkPath: string; useSSO: boolean };
}

const MarketingWidget: React.FC<MarketingWidgetProps> = ({
	widgetDetails = [],
	editMode = false,
	widgets = [],
	setWidgets = () => {},
	setSnackbar = () => {},
	t,
	user,
	editWidgetHeight = null,
	defaultWidgetStyles,
	widgetCustomizationData,
	isInPreviewMode,
	editModalInputFields,
	setEditModalInputFields,
	setWidgetCustomizationData,
	setWidgetFinishStatus,
	widgetFinishStatus,
	currentBreakpoint,
	widgetDrawerThemeSettings,
	isLoading = false,
	selectUserProductAccess,
	handleLinkClick = () => {},
	selectLaunchpadSettingsData,
	checkAndAdjustLinkPath = () => ({ linkPath: '', useSSO: false }),
}) => {
	const userProductAccess: UserProductAccess = useSelector(selectUserProductAccess);

	const [activeSection, setActiveSection] = useState(
		defaultWidgetStyles?.sections?.[1]?.id || '',
	);

	useEffect(() => {
		if (userProductAccess?.client === false) {
			setActiveSection('socialMedia');
		}
	}, [userProductAccess]);

	const handleSectionClick = (section: string): void => {
		if (
			((section !== 'socialMedia' && section !== 'print') ||
				!userProductAccess?.socialBayAccessProhibited) &&
			(section !== 'print' || !userProductAccess?.allOfDesignStudioDisabled)
		) {
			setActiveSection(section);
		}
	};

	const iconName =
		(isInPreviewMode
			? widgetCustomizationData?.headerIcon
			: defaultWidgetStyles?.headerIcon
		)?.name || 'BsMegaphone';
	const IconComponent = BsIcons[iconName as keyof typeof BsIcons] || BsIcons.BsMegaphone;

	const isMobile = ['xs', 'xxs', 'xxxs'].includes(currentBreakpoint || '');

	const getDynamicWidth = (element: string): string | null => {
		if (element === 'headerSkeleton') {
			switch (currentBreakpoint) {
				case 'xxxs':
					return '130px';
				case 'xxs':
					return '160px';
				case 'xs':
					return '200px';
				default:
					return '263px';
			}
		}
		if (element === 'headerSectionSkeleton') {
			switch (currentBreakpoint) {
				case 'xxxs':
					return '130px';
				case 'xxs':
					return '160px';
				case 'xs':
					return '200px';
				default:
					return '290px';
			}
		}
		if (element === 'mobileSectionSkeleton') {
			switch (currentBreakpoint) {
				case 'xxxs':
					return '196px';
				case 'xxs':
					return '160px';
				case 'xs':
					return '200px';
				default:
					return '290px';
			}
		}

		return null;
	};

	const handleSocialBayAccountSetup = (): void => {
		const smcredirect = document.getElementById('smcredirect') as HTMLFormElement | null;
		if (smcredirect) {
			smcredirect.submit();
		}
	};

	return (
		<LaunchpadWidgetWrapper
			defaultWidgetStyles={defaultWidgetStyles}
			editModalInputFields={editModalInputFields}
			editMode={editMode}
			editWidgetHeight={editWidgetHeight}
			handleLinkClick={handleLinkClick}
			hideHeader={true}
			isTitleBlock={true}
			selectLaunchpadSettingsData={selectLaunchpadSettingsData}
			setEditModalInputFields={setEditModalInputFields}
			setSnackbar={setSnackbar}
			setWidgetCustomizationData={setWidgetCustomizationData}
			setWidgetFinishStatus={setWidgetFinishStatus}
			setWidgets={setWidgets}
			t={t}
			widgetCustomizationData={widgetCustomizationData}
			widgetDetails={widgetDetails}
			widgetDrawerThemeSettings={widgetDrawerThemeSettings}
			widgetFinishStatus={widgetFinishStatus}
			widgets={widgets}
		>
			<div
				className={`bay-widget h-100 p-2 d-flex ${
					isMobile ? 'flex-column' : 'gap-4'
				}`}
			>
				<div
					className={`d-flex flex-column gap-3 ${
						isMobile ? 'w-100' : 'w-50 pe-4 border-end border-secondary-subtle'
					}`}
				>
					<div className="d-flex align-items-center gap-3">
						{isLoading ? (
							<Skeleton borderRadius={4} height={66} width={66} />
						) : (
							<div
								className="marketing-widget-icon d-flex align-items-center justify-content-center rounded"
								style={{
									backgroundColor: isInPreviewMode
										? widgetCustomizationData?.iconBgColor
										: defaultWidgetStyles?.iconBgColor,
								}}
							>
								<IconComponent
									size={32}
									style={{
										color: isInPreviewMode
											? widgetCustomizationData?.iconColor
											: defaultWidgetStyles?.iconColor,
									}}
								/>
							</div>
						)}
						{isLoading ? (
							<Skeleton
								borderRadius={2}
								height={40}
								width={getDynamicWidth('headerSkeleton') ?? undefined}
							/>
						) : (
							<h2 className="fs-4 fw-semibold m-0">
								{isInPreviewMode
									? widgetCustomizationData?.headerTitle
									: defaultWidgetStyles?.headerTitle}
							</h2>
						)}
					</div>

					<div className="d-flex flex-column gap-1">
						{(isInPreviewMode
							? widgetCustomizationData?.sections
							: defaultWidgetStyles?.sections
						)
							?.filter((section) => {
								if (!isLoading) {
									const currentPlatform = user?.platform || 'client';
									const hasClientAccess = userProductAccess?.client;
									const hasSocialAccess = userProductAccess?.social;

									if (currentPlatform === 'client') {
										if (hasClientAccess && hasSocialAccess) {
											return ['socialMedia', 'email', 'print'].includes(
												section.id,
											);
										} else {
											return section.id === 'email';
										}
									} else if (currentPlatform === 'social') {
										if (hasClientAccess) {
											return ['socialMedia', 'email', 'print'].includes(
												section.id,
											);
										} else {
											return ['socialMedia', 'print'].includes(section.id);
										}
									}
								}
								return true;
							})
							.map((section) => (
								<div key={section.id} className="marketing-block-item">
									{((section.id === 'socialMedia' || section.id === 'print') &&
										userProductAccess?.socialBayAccessProhibited) ||
									(section.id === 'print' &&
										userProductAccess?.allOfDesignStudioDisabled) ? (
										<BayTooltip
											disabled={isLoading}
											overlay={t('noAccess')}
											placement="top"
										>
											<div
												className={`d-flex justify-content-between align-items-center rounded p-3 py-2 cursor-pointer ${
													activeSection === section.id ? 'bg-blue-50' : ''
												}`}
											>
												{isLoading ? (
													<Skeleton
														borderRadius={2}
														height={50}
														width={getDynamicWidth('headerSectionSkeleton') ?? undefined}
													/>
												) : (
													<div className="d-flex flex-column gap-1">
														<h3
															className="m-0 p-1 fw-semibold"
															style={{
																color:
																	activeSection === section.id
																		? isInPreviewMode
																			? widgetCustomizationData?.linkColor
																			: defaultWidgetStyles?.linkColor
																		: '#97989F',
															}}
														>
															{section.header}
														</h3>
														<p className="m-0 p-1 text-secondary small fs-6">
															{section.description}
														</p>
													</div>
												)}
												{isMobile ? (
													activeSection === section.id ? (
														<BsChevronUp
															className="marketing-widget-chevron"
															size={16}
															style={{
																color: isInPreviewMode
																	? widgetCustomizationData?.linkColor
																	: defaultWidgetStyles?.linkColor,
															}}
														/>
													) : (
														<BsChevronDown
															className="marketing-widget-chevron"
															size={16}
															style={{
																color: '#6c757d',
															}}
														/>
													)
												) : isLoading ? (
													<Skeleton borderRadius={2} height={16} width={16} />
												) : (
													<BsChevronRight
														size={16}
														style={{
															color:
																activeSection === section.id
																	? isInPreviewMode
																		? widgetCustomizationData?.linkColor
																		: defaultWidgetStyles?.linkColor
																	: '#6c757d',
														}}
													/>
												)}
											</div>
										</BayTooltip>
									) : (
										<div
											className={`d-flex justify-content-between align-items-center rounded p-3 py-2 cursor-pointer ${
												activeSection === section.id ? 'bg-blue-50' : ''
											}`}
											onClick={() => handleSectionClick(section.id)}
										>
											<div>
												{isLoading ? (
													<Skeleton
														borderRadius={2}
														height={50}
														width={getDynamicWidth('headerSectionSkeleton') ?? undefined}
													/>
												) : (
													<div className="d-flex flex-column gap-1">
														<h3
															className="m-0 p-1 fw-semibold"
															style={{
																color:
																	activeSection === section.id
																		? isInPreviewMode
																			? widgetCustomizationData?.linkColor
																			: defaultWidgetStyles?.linkColor
																		: '#2F323E',
															}}
														>
															{section.header}
														</h3>
														<p className="m-0 p-1 text-secondary small fs-6">
															{section.description}
														</p>
													</div>
												)}
											</div>
											{isMobile ? (
												isLoading ? (
													<Skeleton borderRadius={2} height={16} width={16} />
												) : activeSection === section.id ? (
													<BsChevronUp
														className="marketing-widget-chevron"
														size={16}
														style={{
															color: isInPreviewMode
																? widgetCustomizationData?.linkColor
																: defaultWidgetStyles?.linkColor,
														}}
													/>
												) : isLoading ? (
													<Skeleton borderRadius={2} height={16} width={16} />
												) : (
													<BsChevronDown
														className="marketing-widget-chevron"
														size={16}
														style={{
															color: '#6c757d',
														}}
													/>
												)
											) : isLoading ? (
												<Skeleton borderRadius={2} height={16} width={16} />
											) : (
												<BsChevronRight
													size={16}
													style={{
														color:
															activeSection === section.id
																? isInPreviewMode
																	? widgetCustomizationData?.linkColor
																	: defaultWidgetStyles?.linkColor
																: '#6c757d',
													}}
												/>
											)}
										</div>
									)}

									{isMobile && activeSection === section.id && (
										<div className="ps-3 pe-3 pb-3">
											{isLoading
												? Array(8)
														.fill(null)
														.map((_, index) => (
															<div
																key={`skeleton-mobile-item-${index}`}
																className="d-flex flex-column"
															>
																<Skeleton
																	borderRadius={2}
																	height={24}
																	width={getDynamicWidth(
																		'mobileSectionSkeleton',
																	) ?? undefined}
																/>
															</div>
														))
												: (() => {
														const shouldShowPopover =
															user.platform === 'client' &&
															(user.ActingAsBAY_ACCOUNT_ID === '' ||
																user.ActingAsBAY_ACCOUNT_ID === 0) &&
															activeSection !== 'email';

														const sectionContent = section.options.map(
															(optionGroup, index) => (
																<div
																	key={index}
																	className="d-flex flex-column gap-3 mt-3"
																>
																	<div className="d-flex flex-row gap-2">
																		{optionGroup.designStudio && (
																			<>
																				<BsFileRichtext
																					color="#191c29"
																					size={16}
																				/>
																				<span
																					className="text-uppercase"
																					style={{
																						fontSize: '12px',
																						color: '#191c29',
																						fontWeight: '600',
																					}}
																				>
																					{optionGroup.designStudio}
																				</span>
																				<div
																					className="border-start border-secondary-subtle"
																					style={{ height: '16px' }}
																				/>
																			</>
																		)}
																		<span className="text-uppercase text-secondary fw-semibold small mb-3">
																			{optionGroup.title}
																		</span>
																	</div>
																	<div className="d-flex flex-column gap-3">
																		{optionGroup.links.map((link) => {
																			if (!link.isVisible) return null;

																			const adjustedLink =
																				checkAndAdjustLinkPath({
																					linkPath: link.path,
																					activeSection,
																				});

																			if (
																				(activeSection === 'socialMedia' &&
																					userProductAccess?.allOfDesignStudioDisabled &&
																					optionGroup.designStudio) ||
																				(activeSection === 'print' &&
																					userProductAccess?.allOfDesignStudioPrintOrderDisabled &&
																					(link.key === 'orderPostcards' ||
																						link.key === 'myOrders'))
																			) {
																				return (
																					<BayTooltip
																						key={link.key}
																						disabled={isLoading}
																						overlay={
																							optionGroup.designStudio
																								? t('noAccessDesignStudio')
																								: t('noAccessOrderPrint')
																						}
																						placement="top"
																					>
																						<span
																							className="d-inline-block"
																							style={{ width: 'fit-content' }}
																						>
																							<a
																								key={link.key}
																								className="text-decoration-none d-inline-block"
																								href=""
																								style={{
																									color: isInPreviewMode
																										? widgetCustomizationData?.linkColor
																										: defaultWidgetStyles?.linkColor,
																									width: 'fit-content',
																									pointerEvents: 'none',
																									opacity: 0.5,
																								}}
																								target="_blank"
																								onClick={(e) => {
																									e.preventDefault();
																									handleLinkClick(
																										e,
																										adjustedLink.linkPath,
																										adjustedLink.useSSO,
																									);
																								}}
																							>
																								{link.label}
																							</a>
																						</span>
																					</BayTooltip>
																				);
																			} else if (!shouldShowPopover) {
																				return (
																					<a
																						key={link.key}
																						className="text-decoration-none d-inline-block"
																						href={adjustedLink.linkPath}
																						style={{
																							color: isInPreviewMode
																								? widgetCustomizationData?.linkColor
																								: defaultWidgetStyles?.linkColor,
																							width: 'fit-content',
																						}}
																						target="_blank"
																						onClick={(e) => {
																							e.preventDefault();
																							handleLinkClick(
																								e,
																								adjustedLink.linkPath,
																								adjustedLink.useSSO,
																							);
																						}}
																					>
																						{link.label}
																					</a>
																				);
																			} else {
																				return (
																					<span
																						key={link.key}
																						className="text-decoration-none d-inline-block cursor-pointer"
																						style={{
																							color: isInPreviewMode
																								? widgetCustomizationData?.linkColor
																								: defaultWidgetStyles?.linkColor,
																							width: 'fit-content',
																							opacity: 0.7,
																						}}
																					>
																						{link.label}
																					</span>
																				);
																			}
																		})}
																	</div>
																</div>
															),
														);

														return shouldShowPopover ? (
															<OverlayTrigger
																disabled={isLoading}
																overlay={
																	<Popover id="popover-mobile-section">
																		<Popover.Body>
																			<div className="d-flex flex-column gap-3">
																				<p className="m-0 fw-semibold">{t('accountSetupRequired')}</p>
																				<p className="m-0">{t('accountSetupRequiredDesc')}</p>
																				<span
																					className="text-blue-600 fw-semibold d-flex align-items-center cursor-pointer"
																					onClick={() =>
																						handleSocialBayAccountSetup()
																					}
																				>
																					<span>
																						Complete Account Setup{' '}
																						<BsArrowRight
																							className="ms-1"
																							size={12}
																						/>
																					</span>
																				</span>
																			</div>
																		</Popover.Body>
																	</Popover>
																}
																placement="top"
																trigger="click"
															>
																<div className="d-flex flex-column gap-3">
																	{sectionContent}
																</div>
															</OverlayTrigger>
														) : (
															<div className="d-flex flex-column gap-3">
																{sectionContent}
															</div>
														);
													})()}
										</div>
									)}
								</div>
							))}
					</div>
				</div>

				{!isMobile && (
					<div className="d-flex flex-column gap-4 w-50">
						{isLoading
							? Array(8)
									.fill(null)
									.map((_, index) => (
										<div
											key={`skeleton-item-${index}`}
											className="d-flex flex-column"
										>
											<Skeleton borderRadius={2} height={24} width={341.75} />
										</div>
									))
							: (() => {
									const shouldShowPopover =
										user.platform === 'client' &&
										(user.ActingAsBAY_ACCOUNT_ID === '' ||
											user.ActingAsBAY_ACCOUNT_ID === 0) &&
										activeSection !== 'email';

									const sectionContent = (
										isInPreviewMode
											? widgetCustomizationData?.sections
											: defaultWidgetStyles?.sections
									)
										?.find((section) => section.id === activeSection)
										?.options.map((optionGroup, index) => (
											<div key={index} className="d-flex flex-column gap-3">
												<div className="d-flex flex-row gap-2">
													{optionGroup.designStudio && (
														<>
															<BsFileRichtext color="#191c29" size={16} />
															<span
																className="text-uppercase"
																style={{
																	fontSize: '12px',
																	color: '#191c29',
																	fontWeight: '600',
																}}
															>
																{optionGroup.designStudio}
															</span>
															<div
																className="border-start border-secondary-subtle"
																style={{ height: '16px' }}
															/>
														</>
													)}
													<span className="text-uppercase text-secondary fw-semibold small mb-3">
														{optionGroup.title}
													</span>
												</div>
												<div className="d-flex flex-column gap-3">
													{optionGroup.links.map((link) => {
														if (!link.isVisible) return null;

														const adjustedLink = checkAndAdjustLinkPath({
															linkPath: link.path,
															activeSection,
														});

														if (
															(activeSection === 'socialMedia' &&
																userProductAccess?.allOfDesignStudioDisabled &&
																optionGroup.designStudio) ||
															(activeSection === 'print' &&
																userProductAccess?.allOfDesignStudioPrintOrderDisabled &&
																(link.key === 'orderPostcards' ||
																	link.key === 'myOrders'))
														) {
															return (
																<BayTooltip
																	key={link.key}
																	disabled={isLoading}
																	overlay={
																		optionGroup.designStudio
																			? t('noAccessDesignStudio')
																			: t('noAccessOrderPrint')
																	}
																	placement="top"
																>
																	<span
																		className="d-inline-block"
																		style={{ width: 'fit-content' }}
																	>
																		<a
																			key={link.key}
																			className="text-decoration-none d-inline-block"
																			href=""
																			style={{
																				color: isInPreviewMode
																					? widgetCustomizationData?.linkColor
																					: defaultWidgetStyles?.linkColor,
																				width: 'fit-content',
																				pointerEvents: 'none',
																				opacity: 0.5,
																			}}
																			target="_blank"
																			onClick={(e) => {
																				e.preventDefault();
																				handleLinkClick(
																					e,
																					adjustedLink.linkPath,
																					adjustedLink.useSSO,
																				);
																			}}
																		>
																			{link.label}
																		</a>
																	</span>
																</BayTooltip>
															);
														} else if (!shouldShowPopover) {
															return (
																<a
																	key={link.key}
																	className="text-decoration-none d-inline-block"
																	href={adjustedLink.linkPath}
																	style={{
																		color: isInPreviewMode
																			? widgetCustomizationData?.linkColor
																			: defaultWidgetStyles?.linkColor,
																		width: 'fit-content',
																	}}
																	target="_blank"
																	onClick={(e) => {
																		e.preventDefault();
																		handleLinkClick(
																			e,
																			adjustedLink.linkPath,
																			adjustedLink.useSSO,
																		);
																	}}
																>
																	{link.label}
																</a>
															);
														} else {
															return (
																<span
																	key={link.key}
																	className="text-decoration-none d-inline-block cursor-pointer"
																	style={{
																		color: isInPreviewMode
																			? widgetCustomizationData?.linkColor
																			: defaultWidgetStyles?.linkColor,
																		width: 'fit-content',
																		opacity: 0.7,
																	}}
																>
																	{link.label}
																</span>
															);
														}
													})}
												</div>
											</div>
										));

									return shouldShowPopover ? (
										<OverlayTrigger
											disabled={isLoading}
											overlay={
												<Popover id="popover-desktop-section">
													<Popover.Body>
														<div className="d-flex flex-column gap-3">
															<p className="m-0 fw-semibold">{t('accountSetupRequired')}</p>
															<p className="m-0">{t('accountSetupRequiredDesc')}</p>
															<span
																className="text-blue-600 fw-semibold d-flex align-items-center cursor-pointer"
																onClick={() => handleSocialBayAccountSetup()}
															>
																<span>
																	Complete Account Setup{' '}
																	<BsArrowRight className="ms-1" size={12} />
																</span>
															</span>
														</div>
													</Popover.Body>
												</Popover>
											}
											placement="left"
											trigger="click"
										>
											<div className="d-flex flex-column gap-3">
												{sectionContent}
											</div>
										</OverlayTrigger>
									) : (
										<div className="d-flex flex-column gap-3">
											{sectionContent}
										</div>
									);
								})()}
					</div>
				)}
			</div>
		</LaunchpadWidgetWrapper>
	);
};

export default MarketingWidget;
